import React, { useState } from 'react';
import { Copy, AlertTriangle, Check } from 'lucide-react';
import { Modal } from '../lib/Modal';
import { api, formatApiError } from '../lib/api';

export function MenuCopyFromLocationModal({ open, onClose, locations, currentRestaurantId, onCopied }) {
    const [sourceId, setSourceId] = useState('');
    const [replace, setReplace] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const otherLocations = (locations || []).filter((l) => l.id !== currentRestaurantId);

    const handleClose = () => {
        setSourceId('');
        setReplace(false);
        setError('');
        setBusy(false);
        onClose();
    };

    const handleCopy = async () => {
        if (!sourceId) return;
        if (replace && !window.confirm('Ești sigur că vrei să ÎNLOCUIEȘTI complet meniul actual? Toate categoriile și produsele actuale vor fi ȘTERSE definitiv.')) {
            return;
        }
        setBusy(true);
        setError('');
        try {
            const { data } = await api.post('/r/menu/copy-from-location', {
                source_restaurant_id: sourceId,
                replace,
            });
            onCopied?.(data);
            handleClose();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={open} title="Copiază meniu din altă locație" onClose={handleClose} maxWidth={600}>
            {otherLocations.length === 0 ? (
                <div style={{ color: 'var(--text-muted)' }}>
                    Nu ai alte locații înregistrate. Adaugă o locație nouă din pagina <strong>Locații</strong>.
                </div>
            ) : (
                <>
                    <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: '1rem', fontSize: '0.92rem', lineHeight: 1.55 }}>
                        Copiază toate categoriile, subcategoriile și produsele din altă locație a ta în locația curentă. Gratuit, fără AI — copiere directă din baza de date.
                    </p>

                    <div style={{ marginBottom: '1rem' }}>
                        <label className="label" htmlFor="copy-source">Locația sursă</label>
                        <select
                            id="copy-source"
                            className="input"
                            value={sourceId}
                            onChange={(e) => setSourceId(e.target.value)}
                            data-testid="copy-source"
                        >
                            <option value="">— Alege locația —</option>
                            {otherLocations.map((loc) => (
                                <option key={loc.id} value={loc.id}>
                                    {loc.name || loc.slug}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border-faint)' }}>
                        <div className="kicker" style={{ marginBottom: 8 }}>Strategie</div>
                        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 8 }}>
                            <input
                                type="radio"
                                name="copy-strategy"
                                checked={!replace}
                                onChange={() => setReplace(false)}
                                style={{ marginTop: 4 }}
                                data-testid="copy-mode-append"
                            />
                            <span>
                                <strong style={{ display: 'block' }}>Adaugă la meniul existent</strong>
                                <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem', lineHeight: 1.4 }}>
                                    Categoriile și produsele din sursă se adaugă lângă cele existente. Recomandat dacă meniul actual e gol sau parțial.
                                </span>
                            </span>
                        </label>
                        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="copy-strategy"
                                checked={replace}
                                onChange={() => setReplace(true)}
                                style={{ marginTop: 4 }}
                                data-testid="copy-mode-replace"
                            />
                            <span>
                                <strong style={{ display: 'block', color: 'var(--neon-magenta)' }}>Înlocuiește meniul actual</strong>
                                <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem', lineHeight: 1.4 }}>
                                    Șterge TOT meniul curent, apoi copiază. Util pentru o locație nouă.
                                </span>
                            </span>
                        </label>
                    </div>

                    {replace && sourceId && (
                        <div style={{ padding: '0.75rem 0.9rem', borderRadius: 10, background: 'rgba(255, 46, 146, 0.06)', border: '1px solid rgba(255, 46, 146, 0.3)', display: 'flex', gap: 10, marginBottom: '1rem' }}>
                            <AlertTriangle size={18} color="var(--neon-magenta)" style={{ flexShrink: 0, marginTop: 2 }} />
                            <div style={{ fontSize: '0.88rem' }}>
                                Acțiunea șterge ireversibil toate categoriile și produsele din locația curentă. Imaginile produselor rămân (sunt stocate separat).
                            </div>
                        </div>
                    )}

                    {error && (
                        <div style={{ marginBottom: '1rem', padding: '0.75rem 0.9rem', borderRadius: 10, background: 'rgba(255, 46, 146, 0.06)', border: '1px solid rgba(255, 46, 146, 0.3)', color: 'var(--text)', fontSize: '0.88rem' }}>
                            {error}
                        </div>
                    )}

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCopy}
                        disabled={busy || !sourceId}
                        style={{ width: '100%' }}
                        data-testid="copy-confirm"
                    >
                        {busy ? <span className="spinner" /> : <Check size={16} />} Copiază meniul
                    </button>
                </>
            )}
        </Modal>
    );
}
